import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import {
  ApolloCache,
  DefaultContext,
  MutationTuple,
  OperationVariables,
  useMutation,
} from "@apollo/client";
import { logEvent } from "../../../helpers/analytics";
import { 
  ProjectOutsourcingFormData,
  UserInfoFormData,
  ContactInfoFormData,
  ProjectDetailsFormData,
  ProjectPreferencesFormData,
} from "../schemas/project-outsourcing.schema";
import { gql } from "../../../__generated__";
import {
  CreateProjectOutsourcingTaskMutation,
  ProjectOutsourcingDto,
  Exact,
} from "../../../__generated__/graphql";

export const CREATE_PROJECT_OUTSOURCING_TASK = gql(`
  mutation createProjectOutsourcingTask($projectOutsourcingDto: ProjectOutsourcingDto!) {
    createProjectOutsourcingTask(projectOutsourcingDto: $projectOutsourcingDto)
  }
`);

interface ProjectOutsourcingContextType {
  currentStep: number;
  isLoading: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  submitForm: MutationTuple<
    CreateProjectOutsourcingTaskMutation,
    Exact<{ projectOutsourcingDto: ProjectOutsourcingDto }>,
    DefaultContext,
    ApolloCache<any>
  >[0];
  resetForm: () => void;
  updateUserInfo: (data: UserInfoFormData) => void;
  updateContactInfo: (data: ContactInfoFormData) => void;
  updateProjectDetails: (data: ProjectDetailsFormData) => void;
  updateProjectPreferences: (data: ProjectPreferencesFormData) => void;
  getAllFormData: () => Partial<ProjectOutsourcingFormData>;
  getUserInfoData: () => Partial<UserInfoFormData>;
  getContactInfoData: () => Partial<ContactInfoFormData>;
  getProjectDetailsData: () => Partial<ProjectDetailsFormData>;
  getProjectPreferencesData: () => Partial<ProjectPreferencesFormData>;
}

const ProjectOutsourcingContext =
  createContext<ProjectOutsourcingContextType | undefined>(undefined);

export const useProjectOutsourcing = (): ProjectOutsourcingContextType => {
  const context = useContext(ProjectOutsourcingContext);
  if (!context) {
    throw new Error(
      "useProjectOutsourcing must be used within a ProjectOutsourcingProvider"
    );
  }
  return context;
};

export const ProjectOutsourcingProvider = ({
  children,
}: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<ProjectOutsourcingFormData>>({});

  const [createProjectOutsourcingTask, { loading }] = useMutation(
    CREATE_PROJECT_OUTSOURCING_TASK
  );

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
      logEvent("project_outsourcing_step_changed", { step });
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
      logEvent("project_outsourcing_next_step", {
        step: currentStep + 1,
      });
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      logEvent("project_outsourcing_previous_step", {
        step: currentStep - 1,
      });
    }
  }, [currentStep]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData({});
    logEvent("project_outsourcing_reset");
  }, []);

  const updateUserInfo = useCallback((data: UserInfoFormData) => {
    setFormData((prev: Partial<ProjectOutsourcingFormData>) => ({ ...prev, ...data }));
    logEvent("project_outsourcing_user_info_updated");
  }, []);

  const updateContactInfo = useCallback((data: ContactInfoFormData) => {
    setFormData((prev: Partial<ProjectOutsourcingFormData>) => ({ ...prev, ...data }));
    logEvent("project_outsourcing_contact_info_updated");
  }, []);

  const updateProjectDetails = useCallback((data: ProjectDetailsFormData) => {
    setFormData((prev: Partial<ProjectOutsourcingFormData>) => ({ ...prev, ...data }));
    logEvent("project_outsourcing_project_details_updated");
  }, []);

  const updateProjectPreferences = useCallback((data: ProjectPreferencesFormData) => {
    setFormData((prev: Partial<ProjectOutsourcingFormData>) => ({ ...prev, ...data }));
    logEvent("project_outsourcing_project_preferences_updated");
  }, []);

  const getAllFormData = useCallback(() => {
    return formData;
  }, [formData]);

  const getUserInfoData = useCallback(() => {
    return {
      userType: formData.userType,
      firstName: formData.firstName,
      lastName: formData.lastName,
      organization: formData.organization,
      roleInOrganization: formData.roleInOrganization,
      industry: formData.industry,
    };
  }, [formData]);

  const getContactInfoData = useCallback(() => {
    return {
      email: formData.email,
      phone: formData.phone,
    };
  }, [formData]);

  const getProjectDetailsData = useCallback(() => {
    return {
      projectType: formData.projectType,
      managementType: formData.managementType,
      description: formData.description,
      keySkillsRequired: formData.keySkillsRequired,
      expectedTeamSize: formData.expectedTeamSize,
    };
  }, [formData]);

  const getProjectPreferencesData = useCallback(() => {
    return {
      challengesToAvoid: formData.challengesToAvoid,
      otherProjectChallenges: formData.otherProjectChallenges,
    };
  }, [formData]);

  const contextValue: ProjectOutsourcingContextType = {
    currentStep,
    isLoading: loading,
    goToStep,
    nextStep,
    previousStep,
    submitForm: createProjectOutsourcingTask,
    resetForm,
    updateUserInfo,
    updateContactInfo,
    updateProjectDetails,
    updateProjectPreferences,
    getAllFormData,
    getUserInfoData,
    getContactInfoData,
    getProjectDetailsData,
    getProjectPreferencesData,
  };

  return (
    <ProjectOutsourcingContext.Provider value={contextValue}>
      {children}
    </ProjectOutsourcingContext.Provider>
  );
};